import { StoredState, SettingsState, CountryData } from './types';

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// GLOBAL CLEANING & UTILITY TOOLS
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export const LOCAL_STORAGE_KEY = 'PreisExpert_Data_V2';

export const forceNumber = (val: any): number => {
    if (val === null || val === undefined || val === '') return 0;
    if (typeof val === 'number') return val;
    const str = val.toString();
    const clean = str.replace(/\./g, '').replace(',', '.');
    const num = parseFloat(clean);
    return isNaN(num) ? 0 : num;
};

export const safeDiv = (numerator: number, denominator: number): number => {
    const num = Number(numerator);
    const den = Number(denominator);
    if (!den || isNaN(num) || isNaN(den)) return 0;
    return num / den;
};

export const generateSimpleId = (): string => `id_${Date.now().toString(36)}${Math.random().toString(36).substr(2, 9)}`;

export const generateTeamMemberId = (): string => `id_mgu${Math.random().toString(36).substr(2, 15)}`;

export const deriveCountrySettings = (settings: SettingsState): CountryData => {
    const id = settings.selectedCountryId;
    const base = settings.countries[id] || settings.countries.DE;
    const overrides = settings.countryOverrides?.[id] || {};
    return { ...base, ...overrides };
};

export const mergeArrayData = <T extends { [key: string]: any }>(defaultArray: T[], loadedArray: T[] | undefined, keyField: keyof T = 'monat'): T[] => {
    if (!loadedArray || !Array.isArray(loadedArray) || loadedArray.length === 0) return defaultArray;
    const loadedMap = new Map(loadedArray.map(item => [item[keyField], item]));
    return defaultArray.map(defaultItem => {
        const loadedItem = loadedMap.get(defaultItem[keyField]);
        return loadedItem ? { ...defaultItem, ...loadedItem } : defaultItem;
    });
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// STORAGE HELPERS
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const cleanLoadedData = (data: StoredState): StoredState => {
    if (data.clientData && data.clientData.mitarbeiterLeistung) {
        Object.keys(data.clientData.mitarbeiterLeistung).forEach(id => {
            if (data.clientData.mitarbeiterLeistung[id]) {
                delete (data.clientData.mitarbeiterLeistung[id] as any).calculatedAvgUmsatzauslastung;
                delete (data.clientData.mitarbeiterLeistung[id] as any).calculatedAvgEfficiency;
                delete (data.clientData.mitarbeiterLeistung[id] as any).calculatedAvgVkFaktor;
                delete (data.clientData.mitarbeiterLeistung[id] as any).calculatedStatusHJ1;
                delete (data.clientData.mitarbeiterLeistung[id] as any).calculatedStatusHJ2;
            }
        });
    }
    return data;
};

export const loadFromLocalStorage = (): StoredState | null => { 
    try { 
        const s = localStorage.getItem(LOCAL_STORAGE_KEY); 
        if (!s) return null;
        const parsed = JSON.parse(s);
        return cleanLoadedData(parsed);
    } catch (e) { return null; }
};

let pendingStorageTimer: ReturnType<typeof setTimeout> | null = null;
let latestPendingStorageData: StoredState | null = null;

export const saveToLocalStorage = (data: StoredState): void => {
    latestPendingStorageData = data;
    if (pendingStorageTimer) return;
    pendingStorageTimer = setTimeout(() => {
        pendingStorageTimer = null;
        const toWrite = latestPendingStorageData;
        latestPendingStorageData = null;
        if (!toWrite) return;
        try {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(toWrite));
        } catch (e) {
            console.warn("Speichern fehlgeschlagen");
        }
    }, 150);
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// CENTRAL REALITY CORE (DOPPEL-SCHLOSS-MATRIX)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export interface RentabilitaetResult {
    status: 'Kritisch' | 'Grenzwertig' | 'Solide' | 'Gut' | 'Exzellent';
    message: string;
}

export const evaluateRentabilitaet = (gehalt: number, ueberschussProzent: number): RentabilitaetResult => {
    // TODESSTOSS-VETO: Unterdeckung (kalk. Überschuss < 0) → immer Kritisch
    if (ueberschussProzent < 0) {
        return {
            status: 'Kritisch',
            message: 'Veto-Auslösung: Unterdeckung. Der kalkulatorische Überschuss ist negativ — die Kosten sind nicht gedeckt.'
        };
    }

    // ACHSE A: Absolutes Gehalt
    let pointsA = 0;
    if (gehalt < 3000) pointsA = 1;
    else if (gehalt < 4000) pointsA = 2;
    else if (gehalt < 5000) pointsA = 3;
    else pointsA = 4;

    // ACHSE B: Prozentualer Überschuss
    let pointsB = 0;
    if (ueberschussProzent < 5) pointsB = 2;
    else if (ueberschussProzent < 10) pointsB = 3;
    else pointsB = 4;

    // DAS TODESSTOSS-VETO (Gehalt)
    if (pointsA === 1) {
        if (pointsB >= 3) {
            return {
                status: 'Kritisch',
                message: 'Veto-Auslösung: Das Unternehmen wirft Überschuss ab, aber dein fixes Gehalt liegt unter der Armutsgrenze. Die Basis muss stimmen!'
            };
        }
        return {
            status: 'Kritisch',
            message: 'Veto-Auslösung: Kritischer Zustand. Weder das absolute Gehalt noch der Überschuss sind tragfähig.'
        };
    }

    // DIE FUSION
    const total = pointsA + pointsB;

    if (total <= 3) {
        return { 
            status: 'Kritisch', 
            message: 'Kritische Lage: Sowohl das absolute Gehalt als auch der Überschuss sind im Gefahrenbereich.' 
        };
    } else if (total === 4) {
        return { 
            status: 'Grenzwertig', 
            message: 'Grenzwertig: Das Gehalt sichert dein Überleben, aber dem Unternehmen fehlt die nötige Rücklage (oder umgekehrt). Hier besteht Handlungsbedarf.' 
        };
    } else if (total === 5) {
        return { 
            status: 'Solide', 
            message: 'Solides Fundament: Das Gehalt und die Rücklage sind in Ordnung, bieten aber noch Luft nach oben für mehr Sicherheit.' 
        };
    } else if (total <= 7) {
        return { 
            status: 'Gut', 
            message: 'Gute und gesunde Unternehmensstruktur. Gehalt und Überschuss stehen in einem soliden Verhältnis zueinander.' 
        };
    } else {
        return { 
            status: 'Exzellent', 
            message: 'Exzellente Struktur! Sehr gesunder Überschuss gepaart mit einem hervorragenden absoluten Gehalt.' 
        };
    }
};